import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
    isYoutubeVideoUrl,
    ReleaseNotesDialogInput,
    ReleaseNoteTourStepView,
    toYoutubeEmbedUrl,
} from '../release-notes';
import { buildReleaseNoteTourSteps, getReleaseNoteFeatureKey } from '../release-notes-tour.util';

@Component({
    selector: 'kiene-release-notes-dialog',
    templateUrl: './release-notes-dialog.component.html',
    styleUrls: ['./release-notes-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ReleaseNotesDialogComponent implements OnInit {
    tourSteps: ReleaseNoteTourStepView[] = [];
    currentIndex = 0;

    constructor(
        public dialogRef: MatDialogRef<ReleaseNotesDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ReleaseNotesDialogInput,
        private readonly router: Router,
        private readonly sanitizer: DomSanitizer,
    ) {}

    ngOnInit(): void {
        this.tourSteps = buildReleaseNoteTourSteps(this.data.versions);
    }

    get currentStep(): ReleaseNoteTourStepView | null {
        return this.tourSteps[this.currentIndex] ?? null;
    }

    get isFirstStep(): boolean {
        return this.currentIndex <= 0;
    }

    get isLastStep(): boolean {
        return this.currentIndex >= this.tourSteps.length - 1;
    }

    get progressPercent(): number {
        const step = this.currentStep;
        if (!step || step.globalTotalSteps <= 0) {
            return 0;
        }
        return (step.globalStepNumber / step.globalTotalSteps) * 100;
    }

    /** Schritte nur des aktuellen Highlights (z. B. 3 Punkte bei „3 von 3“). */
    get currentFeatureSteps(): ReleaseNoteTourStepView[] {
        const step = this.currentStep;
        if (!step) {
            return [];
        }
        const key = this.getFeatureKey(step);
        return this.tourSteps.filter(s => this.getFeatureKey(s) === key);
    }

    get currentFeatureStepIndex(): number {
        const step = this.currentStep;
        if (!step) {
            return 0;
        }
        return Math.max(0, step.stepNumber - 1);
    }

    goToFeatureStep(featureLocalIndex: number): void {
        const target = this.currentFeatureSteps[featureLocalIndex];
        if (!target) {
            return;
        }
        const globalIndex = this.tourSteps.indexOf(target);
        if (globalIndex >= 0) {
            this.currentIndex = globalIndex;
        }
    }

    private getFeatureKey(step: ReleaseNoteTourStepView): string {
        return getReleaseNoteFeatureKey(step);
    }

    previous(): void {
        if (!this.isFirstStep) {
            this.currentIndex--;
        }
    }

    next(): void {
        if (!this.isLastStep) {
            this.currentIndex++;
        }
    }

    verstanden(): void {
        this.dialogRef.close({ markAsSeen: true });
    }

    spaeter(): void {
        this.dialogRef.close({ markAsSeen: false });
    }

    openAllReleaseNotes(): void {
        this.dialogRef.close({ markAsSeen: false });
        this.router.navigate(['/versioninfo']).then();
    }

    tryRoute(route: string): void {
        this.dialogRef.close({ markAsSeen: false });
        this.router.navigate([route]).then();
    }

    openImageFullscreen(imageUrl: string): void {
        window.open(imageUrl, '_blank', 'noopener');
    }

    isYoutubeVideo(url?: string): boolean {
        return !!url && isYoutubeVideoUrl(url);
    }

    getYoutubeEmbedUrl(url: string): SafeResourceUrl {
        return this.sanitizer.bypassSecurityTrustResourceUrl(toYoutubeEmbedUrl(url));
    }
}
